import type { ColorName } from '@tomo/core';
import type { Role } from '@tomo/core/api';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * The app's own IndexedDB database (separate from each project's Y.Doc store, which lives in
 * `tomo:project:<id>` databases managed by y-indexeddb).
 *
 *   projects  registry of projects on this device (home screen, switcher)
 *   blobs     content-addressed asset files (sha-256 → Blob)
 *   kv        small settings (local profile, auth cache, UI state)
 */

export interface LocalProjectEntry {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: ColorName;
  template: string;
  createdAt: number;
  updatedAt: number;
  openedAt: number;
  /**
   * Set when the project lives in a cloud workspace (Pro). `role` is the last role the server
   * granted, so viewers stay read-only while offline too.
   */
  cloud: { workspaceId: string; role?: Role } | null;
  stats?: { collections: number; records: number };
}

export interface StoredBlob {
  hash: string;
  blob: Blob;
  mime: string;
  size: number;
  createdAt: number;
  /** Project ids whose cloud copy already has this blob. */
  uploadedTo?: string[];
}

interface TomoDB extends DBSchema {
  projects: { key: string; value: LocalProjectEntry };
  blobs: { key: string; value: StoredBlob };
  kv: { key: string; value: unknown };
}

let dbp: Promise<IDBPDatabase<TomoDB>> | null = null;

export function db(): Promise<IDBPDatabase<TomoDB>> {
  dbp ??= openDB<TomoDB>('tomo', 1, {
    upgrade(d) {
      if (!d.objectStoreNames.contains('projects')) d.createObjectStore('projects', { keyPath: 'id' });
      if (!d.objectStoreNames.contains('blobs')) d.createObjectStore('blobs', { keyPath: 'hash' });
      if (!d.objectStoreNames.contains('kv')) d.createObjectStore('kv');
    },
  });
  return dbp;
}

export async function kvGet<T>(key: string): Promise<T | undefined> {
  return (await (await db()).get('kv', key)) as T | undefined;
}

export async function kvSet(key: string, value: unknown): Promise<void> {
  await (await db()).put('kv', value, key);
}

export async function kvDelete(key: string): Promise<void> {
  await (await db()).delete('kv', key);
}

/** Ask the browser not to evict our data under storage pressure. */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function storageEstimate(): Promise<{ usage: number; quota: number; persisted: boolean } | null> {
  try {
    const [est, persisted] = await Promise.all([navigator.storage.estimate(), navigator.storage.persisted?.() ?? Promise.resolve(false)]);
    return { usage: est.usage ?? 0, quota: est.quota ?? 0, persisted };
  } catch {
    return null;
  }
}
